import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import StatusCode from '../../utils/StatusCode';

interface Division {
  _id: string;
  divisionName: string;
  divisionType: string;
  streamId?: string;
  classTeacherId?: string;
  isActive: boolean;
  createdBy?: string | { _id: string };
  updatedBy?: Array<string | { _id: string }>;
  created_at: string;
  updated_at: string;
  deleted?: boolean;
}

interface DivisionState {
  data: Division[];
  count: number;
  pageIndex: number;
  size: number;
  search: string;
  sortingData: Array<{ id: string; sort?: boolean; desc?: boolean }>;
  status: StatusCode;
  error: string | null;
}

const initialState: DivisionState = {
  data: [],
  count: 0,
  pageIndex: 1,
  size: 10,
  search: '',
  sortingData: [], // For column sorting (e.g., [{ id: 'divisionName', sort: true, desc: false }])
  status: StatusCode.IDLE,
  error: null
};

const divisionSlice = createSlice({
  name: 'division',
  initialState,
  reducers: {
    // Fetch / Set divisions list
    getDivision(state, action: PayloadAction<Division[]>) {
      state.data = action.payload;
      state.status = StatusCode.SUCCESS;
    },

    // Optional: for single add/update if you want optimistic UI
    addDivision(state, action: PayloadAction<Division>) {
      state.data = [action.payload, ...state.data];
      state.count += 1;
    },

    // Optimistic delete (client-side removal)
    delDivision(state, action: PayloadAction<string>) {
      state.data = state.data.filter((item) => item._id !== action.payload);
      state.count = Math.max(0, state.count - 1);
    },

    // Replace entire list after update (safer than mutating)
    updateDivision(state, action: PayloadAction<Division>) {
      state.data = state.data.map((item) => (item._id === action.payload._id ? action.payload : item));
    },

    // Search
    setDivisionSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
      state.pageIndex = 1; // Reset to first page on search
    },

    // Pagination
    setDivisionTableSize(state, action: PayloadAction<number>) {
      state.size = action.payload;
      state.pageIndex = 1;
    },

    setDivisionPageIndex(state, action: PayloadAction<number>) {
      state.pageIndex = action.payload;
    },

    // Total count from backend
    setDivisionCount(state, action: PayloadAction<number>) {
      state.count = action.payload;
    },

    // Sorting
    setSortingValue(state, action: PayloadAction<Array<{ id: string; sort?: boolean; desc?: boolean }>>) {
      state.sortingData = action.payload;
    },

    // Loading state
    setLoading(state) {
      state.status = StatusCode.LOADING;
    },

    // Error state
    setError(state, action: PayloadAction<string>) {
      state.status = StatusCode.ERROR;
      state.error = action.payload;
    },

    // Full reset (e.g., on logout or navigation away)
    resetDivisions() {
      return initialState;
    }
  }
});

export const {
  getDivision,
  addDivision,
  delDivision,
  updateDivision,
  setDivisionSearch,
  setDivisionTableSize,
  setDivisionPageIndex,
  setDivisionCount,
  setSortingValue,
  setLoading,
  setError,
  resetDivisions
} = divisionSlice.actions;

export default divisionSlice.reducer;
